import { Cross1Icon, GearIcon } from '@radix-ui/react-icons'
import {
  DialogClose,
  DialogTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
} from '~/sotano-kit/primitives/dialog'
import { Button } from '~/sotano-kit/primitives/button'
import { Repository, Team } from '@basehub/data/replicache/index'
import * as React from 'react'
import { useToggleState } from '~/hooks/use-toggle-state'
import { Input, LabeledInput } from '~/sotano-kit/primitives/input'
import { clsx } from 'clsx'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { getId } from '@basehub/utils/id/index'
import { invariant } from '@epic-web/invariant'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useUserStore } from '~/app/_store/user-store'
import {
  RadioGroup,
  RadioGroupProps,
  RadioItemWithLabel,
} from '~/sotano-kit/primitives/radio-group'
import { isHotkey } from 'is-hotkey'

export const RepositorySettingsDialog = ({
  repository,
  team,
}: {
  repository: Repository | PublicRepo
  team: Team | PublicTeam
}) => {
  const teamReplicache = useTeamReplicache().data?.replicache
  const user = useUserStore((s) => s.optimisticUser)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { handleOn, ...dialogState } = useToggleState(false)
  const router = useRouter()
  const focusRef = React.useRef<'title' | 'description'>(undefined)

  React.useEffect(() => {
    if (searchParams.get('settings') === 'true') {
      handleOn()
    }
  }, [searchParams, handleOn])

  const {
    mutate: handleSave,
    error,
    isLoading,
    isError,
    reset,
  } = useMutation(
    ['repoPublicInfoUpdate', repository.id],
    async (formData: FormData) => {
      invariant(teamReplicache, 'Replicache must be defined')

      const title = formData.get('Title')?.toString()
      const description = formData.get('Description')?.toString()
      const previewUrl = formData.get('Website URL')?.toString()
      const gitRepoUrl = formData.get('Git Repository URL')?.toString()
      const visibilityValue = formData.get('Visibility')?.toString()
      invariant(visibilityValue, 'Visibility value must be defined')
      const isPublic = visibilityValue === 'public'

      const currentSlug = repository.slug
      let newSlug: string | undefined

      if (title && title !== repository.title) {
        invariant(user?.id, 'User must be defined')

        newSlug = (
          await teamReplicache.mutate.renameRepository({
            repoId: repository.id,
            newTitle: title,
            isoNow: new Date().toISOString(),
            teamId: team.id,
            eventId: getId(),
            userId: user.id,
          })
        ).newSlug
      }

      await teamReplicache.mutate.updateRepositoryPublicInfo({
        repoId: repository.id,
        description,
        previewUrl,
        gitRepoUrl,
        isPublic,
      })

      if (newSlug) {
        router.push(pathname.replace(currentSlug, newSlug))
      }
    },
    {
      onSuccess: () => {
        toast.success('Repository updated successfully.')
        dialogState.handleOff()
      },
    }
  )

  return (
    <Dialog
      open={dialogState.isOn}
      onOpenChange={(open) => {
        if (searchParams.get('settings') === 'true') {
          // remove query param
          const params = new URLSearchParams(searchParams)
          params.delete('settings')
          router.push(pathname + '?' + params.toString())
        }
        if (!open) focusRef.current = undefined
        reset()

        dialogState.handleToggle()
      }}
    >
      <DialogTrigger asChild>
        <Button
          icon={<GearIcon width={14} height={14} />}
          intent="secondary"
          className="shrink-0 text-xs"
        >
          Settings
        </Button>
      </DialogTrigger>
      <DialogContent
        asChild
        className="!p-5 !min-w-[460px] flex flex-col max-w-96 !rounded-2xl overflow-x-hidden"
      >
        <form action={handleSave}>
          <div className="flex gap-x-2 items-center mb-0.5">
            <h2 className="mr-auto text-lg font-medium leading-normal">
              Settings
            </h2>
            <DialogClose asChild>
              <Button
                unstyled
                focusStyled
                className="text-dark-muted hover:text-dark-dim p-1.5 cursor-default"
              >
                <Cross1Icon />
              </Button>
            </DialogClose>
          </div>
          <TextOption
            name="Title"
            initialValue={repository.title}
            inputProps={{
              required: true,
              autoFocus: !focusRef.current || focusRef.current === 'title',
              onFocus: (e) => {
                if (focusRef.current === 'title') {
                  e.currentTarget.select()
                }
              },
            }}
          />
          <TextOption
            name="Description"
            placeholder="Something about your repository"
            multiLine
            inputProps={{
              autoFocus: focusRef.current === 'description',
              onFocus: (e) => {
                if (focusRef.current === 'description') {
                  e.currentTarget.select()
                }
              },
              onKeyDown: (e) => {
                if (isHotkey('mod+enter', e)) {
                  e.preventDefault()
                  const formData = new FormData(
                    e.currentTarget.form as HTMLFormElement
                  )
                  handleSave(formData)
                }
              },
            }}
            initialValue={repository.description ?? ''}
          />
          <RelevantLinkOption
            placeholder="Link to your website"
            url={repository.previewUrl ?? undefined}
            label="Website URL"
          />
          <RelevantLinkOption
            placeholder="Link to your git repository"
            url={repository.gitRepoUrl ?? undefined}
            label="Git Repository URL"
          />
          <div className="text-xs text-dark-muted flex grow flex-col gap-y-1.5 pt-2.5">
            <span className="font-semibold">Visibility</span>
            <IsPublicRadioGroup isPublic={Boolean(repository.isPublic)} />
          </div>

          <DialogFooter divider>
            {/* <div className="flex items-center justify-end gap-2 mt-9 relative"> */}
            {isError && (
              <span className="flex w-full ml-px text-xs leading-4 gap-x-1 text-dark-error absolute -translate-y-8">
                <span className="truncate">{(error as Error).message}</span>
              </span>
            )}

            <DialogClose asChild>
              <Button type="button" intent="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isLoading}>
              Save
            </Button>
            {/* </div> */}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const TextOption = ({
  name,
  initialValue,
  placeholder,
  type = 'text',
  inputProps,
  multiLine = false,
}: {
  name: string
  initialValue: string
  type?: string
  placeholder?: string
  inputProps?: React.ComponentProps<typeof Input>
  multiLine?: boolean
}) => {
  const id = React.useId()
  const [value, setValue] = React.useState(initialValue)

  return (
    <LabeledInput
      className="!pt-2.5 !pb-2"
      htmlFor={id}
      label={<span className="items-center inline-flex">{name}</span>}
      size="xs"
      name={name}
      blocked={false}
      inputProps={{
        value,
        onChange: (e) => setValue(e.target.value),
        asChild: multiLine,
        id: id,
        autoComplete: 'off',
        type,
        size: 'xs',
        placeholder: typeof placeholder === 'string' ? placeholder : undefined,
        className: clsx(multiLine && 'py-2 min-h-18'),
        children: multiLine ? (
          <textarea className="min-h-20 scroll-py-2 max-h-44 px-3 resize-none" />
        ) : null,
        ...inputProps,
      }}
    />
  )
}

const RelevantLinkOption = ({
  url,
  label,
  inputProps,
  placeholder,
}: {
  url?: string
  label: string
  inputProps?: React.ComponentProps<typeof Input>
  placeholder?: string
}) => {
  return (
    <LabeledInput
      className="!py-2.5 !text-xs"
      blocked={false}
      label={label}
      name={label}
      inputProps={{
        maxLength: 64,
        placeholder,
        autoComplete: 'off',
        size: 'xs',
        defaultValue: url,
        ...inputProps,
      }}
    />
  )
}

export const IsPublicRadioGroup = ({
  isPublic,
  ...props
}: {
  isPublic?: boolean
} & RadioGroupProps) => {
  return (
    <RadioGroup
      // {...baseProps}
      name="Visibility"
      className="flex items-center group/root gap-6 text-xs text-dark-muted"
      defaultValue={isPublic ? 'public' : 'private'}
      // onValueChange={handleChange}
      aria-label="Public or Private"
      {...props}
    >
      <RadioItemWithLabel isActive={isPublic} value="public">
        Public
      </RadioItemWithLabel>
      <RadioItemWithLabel isActive={!isPublic} value="private">
        Private
      </RadioItemWithLabel>
    </RadioGroup>
  )
}
